import { useState } from 'react';
import { Field, LinkField, Text } from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames';
import ModalWrapper from 'helpers/ModalWrapper/ModalWrapper';
import SVG from 'helpers/SVG/SVG';
import Button from 'helpers/Button/Button';
import RichTextA11yWrapper from 'helpers/RichTextA11yWrapper/RichTextA11yWrapper';

export type LocationPlaceholderProps = {
  onModalClose: () => void;
  isModalOpen: boolean;
  locationName: string;
};

export type LocationModalProps = {
  fields: LocationModalField;
} & LocationPlaceholderProps;

export type LocationModalField = {
  CopyText: Field<string>;
  PrimaryButtonText: Field<string>;
  SecondaryButtonText: Field<string>;
  SubTitle: Field<string>;
  Title: Field<string>;
  children: children[];
};

export type children = {
  fields: ChildrenFields;
  name: string;
};

export type ChildrenFields = {
  URL: LinkField;
  State: State;
};

export type State = {
  fields: Statefields;
};

export type Statefields = {
  StateAbbreviation: Field<string>;
  StateName: Field<string>;
};

const LocationModal = (props: LocationModalProps) => {
  const { CopyText, PrimaryButtonText, SecondaryButtonText, SubTitle, Title, children } =
    props?.fields || {};

  const [redirectUrl, setRedirectUrl] = useState<string | undefined>(undefined);
  const [selectedLocationName, setSelectedLocationName] = useState<string>(props?.locationName);

  return props?.isModalOpen ? (
    <ModalWrapper
      onClose={() => {
        setSelectedLocationName(props?.locationName);
        setRedirectUrl(undefined);
        props?.onModalClose();
      }}
      position="center"
    >
      <div className="flex flex-col gap-8 mt-2">
        <div className="choose-location flex flex-col gap-2">
          <Text tag="h3" field={Title} />
          <Text className="p2" tag="p" field={SubTitle} />
        </div>
        <div className="state-list">
          <ul className="flex flex-wrap gap-4">
            {children.map((item, index) => (
              <li key={index}>
                <button
                  onClick={() => {
                    setSelectedLocationName(
                      item?.fields?.State?.fields?.StateName?.value || item?.name
                    );
                    setRedirectUrl(item?.fields?.URL?.value?.href);
                  }}
                  type="button"
                  className={classNames(
                    'block pl-2 py-2 pr-3 border rounded border-light-stroke-primary-variant p3',
                    ' hover:border-light-stroke-primary-variant-high',
                    `${
                      (item?.fields?.State?.fields?.StateName?.value || item?.name).includes(
                        selectedLocationName
                      )
                        ? 'flex p3-medium items-center justify-center gap-1 border-light-stroke-primary-variant bg-light-background-primary hover:bg-light-background-primary-high focus:bg-light-background-primary-highest focus:outline-none text-light-fill-inverse hover:text-light-fill-inverse'
                        : 'hover:text-light-fill-primary text-light-fill-primary hover:p3-medium-underline focus:p3-medium focus:outline focus:outline-stroke-primary-variant-highest focus:no-underline'
                    }`
                  )}
                >
                  {(item?.fields?.State?.fields?.StateName?.value || item?.name).includes(
                    selectedLocationName
                  ) && <SVG className="[&_svg]:h-6 [&_svg]:w-6" svg="icon-pin" />}
                  {item?.fields?.State?.fields?.StateName?.value || item?.name}
                </button>
              </li>
            ))}
          </ul>
        </div>
        <div className="ip-info">
          <RichTextA11yWrapper
            className="text-light-fill-primary-variant p3-regular rtaw"
            field={CopyText}
          />
        </div>
        <div className="buttons border-t">
          <div className="flex flex-col md:flex-row gap-4 md:gap-6 mt-4 md:mt-6 justify-end">
            <Button
              type="secondary"
              tag="button"
              fieldClass="order-last md:order-first"
              label={PrimaryButtonText?.value}
              onClick={() => {
                setRedirectUrl(undefined);
                setSelectedLocationName(props?.locationName);
                props?.onModalClose();
              }}
            />
            <Button
              type="default"
              tag="button"
              className=""
              label={SecondaryButtonText?.value}
              onClick={() => {
                // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                redirectUrl && (window.location.href = redirectUrl);
              }}
            />
          </div>
        </div>
      </div>
    </ModalWrapper>
  ) : (
    <></>
  );
};

export default LocationModal;
